import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';

/**
 * TrackingScripts - Analytics and tracking scripts
 * Only loads in public areas (not for logged-in users)
 * Uses react-helmet-async to inject scripts directly into HTML head
 * Works with react-snap pre-rendering
 * 
 * Configuration:
 * - GTM Container ID: Set via REACT_APP_GTM_ID (separate for each domain)
 *   → GTM loads: Google Analytics (GA4), MS Clarity, CookieYes, and other Google tags
 * 
 * Domains:
 * - tools.dgtia.com → REACT_APP_GTM_ID
 * - studio.dgtia.com → REACT_APP_GTM_ID
 * - dgtia.com → Configure in that project's codebase
 */
export const TrackingScripts = () => {
    const isProduction = process.env.REACT_APP_ENV === 'production';
    const isDev = process.env.REACT_APP_ENV === 'dev';
    const isLoggedIn = useSelector((state) => state.auth?.isLoggedIn || false);
    const consent = useSelector((state) => state.app?.consent);

    const GTM_ID = process.env.REACT_APP_GTM_ID || 'GTM-TB6SPTW3';

    useEffect(() => {
        if ((isProduction || isDev) && !isLoggedIn && consent?.status === 'accepted' && GTM_ID) {
            const existingNoscript = document.querySelector('noscript[data-gtm-noscript]');
            if (existingNoscript) {
                existingNoscript.remove();
            }

            const noscript = document.createElement('noscript');
            noscript.setAttribute('data-gtm-noscript', 'true');
            const iframe = document.createElement('iframe');
            iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
            iframe.height = '0';
            iframe.width = '0';
            iframe.style.display = 'none';
            iframe.style.visibility = 'hidden';
            noscript.appendChild(iframe);
            document.body.insertBefore(noscript, document.body.firstChild);

            return () => {
                const noscriptToRemove = document.querySelector('noscript[data-gtm-noscript]');
                if (noscriptToRemove) {
                    noscriptToRemove.remove();
                }
            };
        }
    }, [GTM_ID, isProduction, isDev, isLoggedIn, consent]);

    // Don't load tracking in local development, for logged-in users, or if consent not given
    if ((!isProduction && !isDev) || isLoggedIn || consent?.status !== 'accepted') {
        return null;
    }

    return (
        <Helmet>
            {GTM_ID && (
                <script>
                    {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${GTM_ID}');`}
                </script>
            )}
        </Helmet>
    );
};

